import { LinksPageConfig } from "../components/links-page/links-page.config";
import type { LinksPageProps } from "../components/links-page/links-page";
import type { Config, ComponentConfig } from "@puckeditor/core";
import { FieldInput } from "./field-input";
import { useFormValue, useFormUpload } from "./form-values-context";
import { CORE_FIELD_COMPONENTS } from "./form-data";
import type { FieldDef, FieldType } from "./form-data";

/**
 * The forms Puck config — one component per field type. Puck `<Render>` (and the
 * Puck editor preview) draw these; each `render` binds the shared FieldInput to
 * the form-values context by the component's `id` (which is the formData.custom
 * key). This is the single field renderer for both the editor and the live form.
 */

interface BaseFieldProps {
  id?: string; // Puck-managed instance id; the formData.custom key
  label: string;
  required?: string; // "yes" | "no"
  helpText?: string;
}
interface OptionFieldProps extends BaseFieldProps {
  options?: { label: string; value: string }[];
}

function FieldBinding({ field }: { field: FieldDef }) {
  const [value, setValue] = useFormValue(field.id);
  const uploadImage = useFormUpload();
  // Vertical rhythm between fields — Puck stacks components flush otherwise.
  // Applies everywhere the config renders: editor canvas, preview, live widget.
  return (
    <div className="jobsite:py-2">
      <FieldInput field={field} value={value} onChange={setValue} uploadImage={uploadImage} />
    </div>
  );
}

const baseFields = {
  label: { type: "text" as const, label: "Label" },
  required: {
    type: "radio" as const,
    label: "Required",
    options: [
      { label: "Required", value: "yes" },
      { label: "Optional", value: "no" },
    ],
  },
  helpText: { type: "text" as const, label: "Help text" },
};

function simpleField(
  type: FieldType,
  label: string,
  defaultLabel: string,
): ComponentConfig<BaseFieldProps> {
  return {
    label,
    fields: { ...baseFields },
    defaultProps: { label: defaultLabel, required: "no" },
    render: ({ id, label, required, helpText }) => (
      <FieldBinding
        field={{ id: id ?? "", type, label, required: required === "yes", helpText }}
      />
    ),
  };
}

function optionField(
  type: FieldType,
  label: string,
  defaultLabel: string,
): ComponentConfig<OptionFieldProps> {
  return {
    label,
    fields: {
      ...baseFields,
      options: {
        type: "array" as const,
        label: "Options",
        arrayFields: {
          label: { type: "text" as const },
          value: { type: "text" as const },
        },
        defaultItemProps: { label: "Option", value: "option" },
      },
    },
    defaultProps: { label: defaultLabel, required: "no", options: [] },
    render: ({ id, label, required, helpText, options }) => (
      <FieldBinding
        field={{ id: id ?? "", type, label, required: required === "yes", helpText, options }}
      />
    ),
  };
}

/**
 * A core contact field (see CORE_FIELD_COMPONENTS): renders like any field, but
 * binds to its canonical top-level formData key instead of the Puck instance id
 * — so dedup/notifications/workflows read the same keys they always have.
 */
function coreField(
  componentType: string,
  paletteLabel: string,
  defaultLabel: string,
): ComponentConfig<BaseFieldProps> {
  const spec = CORE_FIELD_COMPONENTS[componentType];
  return {
    label: paletteLabel,
    fields: { ...baseFields },
    defaultProps: { label: defaultLabel, required: "no" },
    render: ({ label, required, helpText }) => (
      <FieldBinding
        field={{
          id: spec.key,
          type: spec.type,
          label,
          required: required === "yes",
          helpText,
          autoComplete: spec.autoComplete,
        }}
      />
    ),
  };
}

export interface FormFieldComponentProps {
  TextField: BaseFieldProps;
  TextareaField: BaseFieldProps;
  NumberField: BaseFieldProps;
  DateField: BaseFieldProps;
  BooleanField: BaseFieldProps;
  ImageField: BaseFieldProps;
  SelectField: OptionFieldProps;
  MultiSelectField: OptionFieldProps;
}

export interface ContactFormComponentProps extends FormFieldComponentProps {
  NameField: BaseFieldProps;
  EmailField: BaseFieldProps;
  PhoneField: BaseFieldProps;
  AddressField: BaseFieldProps;
  MessageField: BaseFieldProps;
}

/** The contact form has no form-level settings (the booking-only root knobs —
 *  note, "Additional details", step text — don't apply). */
export interface ContactFormRootProps {
  [key: string]: never;
}

/** Form-level ("Page") settings — built-in knobs that aren't per-field. Read by
 *  the booking surfaces via the helpers in form-data.ts. */
export interface FormRootProps {
  noteEnabled?: string; // "yes" | "no" — the "Anything else?" note
  noteLabel?: string;
  noteRequired?: string; // "yes" | "no"
  additionalDetails?: { text: string }[]; // booking-page "Additional details" bullets
  additionalDetailsHeading?: string; // heading over those bullets (default "Additional details")
  /** @deprecated Pre-rename keys (the section was mocked up as "Good to know").
   *  Read-only back-compat: the read helpers fall back to these for booking forms
   *  persisted before the rename; new writes use additionalDetails*. */
  goodToKnow?: { text: string }[];
  goodToKnowHeading?: string;
  heroImageUrl?: string; // booking-page hero card image (overrides the GBP photo)
}

// <Puck>/<Render> accept the base `Config`, but a parameterized
// `Config<Props, Root>` doesn't structurally satisfy that constraint in Puck's
// types. This boundary keeps full per-component type-checking on the literal
// (its argument is typed) while handing callers the base `Config` they accept —
// the one and only widening lives here, not scattered across call sites.
//
// Tracking puckeditor/puck#1519 (Puck's generic config/Fields type-distribution
// bug). Revisit and drop this boundary once a Puck release lets a parameterized
// `Config<Props, Root>` satisfy the base `Config` constraint directly.
function asPuckConfig(config: Config<FormFieldComponentProps, FormRootProps>): Config;
function asPuckConfig(config: Config<ContactFormComponentProps, ContactFormRootProps>): Config;
function asPuckConfig(config: Config<{ LinksPage: LinksPageProps }>): Config;
function asPuckConfig(config: unknown): Config {
  return config as Config;
}

/** The custom-field palette, shared by both configs below. */
const fieldComponents: Config<FormFieldComponentProps>["components"] = {
  TextField: simpleField("text", "Text", "Text field"),
  TextareaField: simpleField("textarea", "Long text", "Message"),
  NumberField: simpleField("number", "Number", "Number"),
  DateField: simpleField("date", "Date", "Date"),
  BooleanField: simpleField("boolean", "Yes / no", "I agree"),
  ImageField: simpleField("image", "Photo upload", "Upload a photo"),
  SelectField: optionField("select", "Dropdown", "Choose one"),
  MultiSelectField: optionField("multiselect", "Multi-select", "Select all that apply"),
};

export const formsPuckConfig: Config = asPuckConfig({
  root: {
    fields: {
      noteEnabled: {
        type: "radio",
        label: "“Anything else?” note",
        options: [
          { label: "Show", value: "yes" },
          { label: "Hide", value: "no" },
        ],
      },
      noteLabel: { type: "text", label: "Note label" },
      noteRequired: {
        type: "radio",
        label: "Note required?",
        options: [
          { label: "Optional", value: "no" },
          { label: "Required", value: "yes" },
        ],
      },
      additionalDetails: {
        type: "array",
        label: "“Additional details” bullets",
        arrayFields: { text: { type: "textarea", label: "Bullet" } },
        defaultItemProps: { text: "" },
      },
    },
    defaultProps: {
      noteEnabled: "no",
      noteLabel: "Anything we should know?",
      noteRequired: "no",
      additionalDetails: [
        { text: "No account or app to download. Just verify by text and you're booked." },
        { text: "See pricing up front, or get a price at the visit before any work begins." },
      ],
      heroImageUrl: "",
    },
    // The form is just its fields — root only carries page-level settings.
    render: ({ children }) => <>{children}</>,
  },
  components: fieldComponents,
});

/**
 * The contact-form Puck config: the same custom-field palette PLUS the core
 * contact fields (Name/Email/Phone/Message → canonical top-level formData
 * keys). A separate config on purpose: the booking builder must never offer
 * core fields — booking identity comes from its OTP flow, and a core key would
 * be dropped by the customFields filter on submit. Booking-only root settings
 * ("Additional details", step text, the note) don't apply here either.
 */
/**
 * The link-page Puck config: one component, whose fields ARE the whole form
 * (wiki/decisions/link-page-form.md). No palette, no core fields — the
 * builder locks structure and the panel edits the LinksPage node.
 */
export const linksPuckConfig: Config = asPuckConfig({
  root: {
    render: ({ children }) => <>{children}</>,
  },
  components: {
    LinksPage: LinksPageConfig,
  },
});

export const contactFormsPuckConfig: Config = asPuckConfig({
  root: {
    render: ({ children }) => <>{children}</>,
  },
  components: {
    // Core fields first so the palette leads with them.
    NameField: coreField("NameField", "Name", "Your Name"),
    EmailField: coreField("EmailField", "Email", "Email Address"),
    PhoneField: coreField("PhoneField", "Phone", "Phone Number"),
    AddressField: coreField("AddressField", "Address", "Service Address"),
    MessageField: coreField("MessageField", "Message", "Your Message"),
    ...fieldComponents,
  },
});
